import PropTypes from 'prop-types';
import { BsWindow } from 'react-icons/bs';

import SectionCard from '#globalComponents/Card/SectionCard';
import Tag from '#globalComponents/Tag/Tag';

import { useTargetingVocabulary } from '../../models/targeting/queries';
import { FACET_LABELS, facetValueLabel } from '../../utils/targetingLabels';

const FACET_ROWS = [
	'businessModel',
	'customerTypes',
	'offerings',
	'specializations',
	'serviceArea',
	'targetIndustries',
];

const TECH_ROWS = [
	{ key: 'cms', label: 'CMS' },
	{ key: 'shop', label: 'Shop' },
	{ key: 'booking', label: 'Terminbuchung' },
	{ key: 'chat', label: 'Chat' },
	{ key: 'jobsEmbed', label: 'Bewerbermanagement' },
	{ key: 'tracking', label: 'Tracking' },
	{ key: 'consent', label: 'Cookie-Banner' },
];

const TECH_FLAGS = [
	{ key: 'hasCareersPage', label: 'Karriereseite' },
	{ key: 'hasOnlineShop', label: 'Onlineshop' },
	{ key: 'hasBookingTool', label: 'Online-Terminbuchung' },
];

const LANGUAGE_LABELS = {
	de: 'Deutsch',
	en: 'Englisch',
	fr: 'Französisch',
	it: 'Italienisch',
	es: 'Spanisch',
	nl: 'Niederländisch',
	pl: 'Polnisch',
};

const asList = (value) => [value].flat().filter((entry) => entry !== null && entry !== undefined && entry !== '');

/**
 * Tag rows per facet: certain values first, then hints the certain values do not already cover.
 * @param {Object|null} facets - Stored facets ({ filterGrade, hints })
 * @returns {Array<{facet: string, certain: string[], hints: string[]}>}
 */
const buildFacetRows = (facets) =>
	FACET_ROWS.map((facet) => {
		const certain = asList(facets?.filterGrade?.[facet]);
		const hints = asList(facets?.hints?.[facet]).filter((value) => !certain.includes(value));
		return { facet, certain, hints };
	}).filter((row) => row.certain.length > 0 || row.hints.length > 0);

const toolLabel = (vocabulary, category, value) => {
	const facetValue = `${category}:${value}`;
	const label = facetValueLabel(vocabulary, 'tech', facetValue);
	return label === facetValue ? value : label;
};

const buildTechEntries = (tech, vocabulary) => [
	...TECH_ROWS.map(({ key, label }) => ({
		label,
		value: asList(tech?.[key])
			.map((value) => toolLabel(vocabulary, key, value))
			.join(', '),
	})).filter((entry) => entry.value),
	...TECH_FLAGS.filter(({ key }) => tech?.[key] === true).map(({ label }) => ({ label, value: 'vorhanden' })),
];

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('de-DE') : null);

const FacetRow = ({ row, vocabulary }) => (
	<div className="flex items-start gap-4 border-b border-bgBase-500 py-2 last:border-0">
		<dt className="w-36 flex-shrink-0 text-sm text-bgBase-800">{FACET_LABELS[row.facet]}</dt>
		<dd className="flex min-w-0 flex-wrap gap-1">
			{row.certain.map((value) => (
				<Tag key={value} label={facetValueLabel(vocabulary, row.facet, value)} tone="primary" />
			))}
			{row.hints.map((value) => (
				<Tag
					key={value}
					prefix="Hinweis"
					label={facetValueLabel(vocabulary, row.facet, value)}
					tone="neutral"
				/>
			))}
		</dd>
	</div>
);

FacetRow.propTypes = {
	row: PropTypes.shape({
		facet: PropTypes.string.isRequired,
		certain: PropTypes.array.isRequired,
		hints: PropTypes.array.isRequired,
	}).isRequired,
	vocabulary: PropTypes.object,
};

const InfoRow = ({ label, value }) => (
	<div className="flex items-start gap-4 border-b border-bgBase-500 py-2 last:border-0">
		<dt className="w-36 flex-shrink-0 text-sm text-bgBase-800">{label}</dt>
		<dd className="min-w-0 text-sm text-text">{value}</dd>
	</div>
);

InfoRow.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string.isRequired,
};

/** "Website-Profil" section of the company detail page: what the website says about the company. */
const CompanyWebsiteProfile = ({ profile }) => {
	const { data: vocabulary } = useTargetingVocabulary();
	const facetRows = buildFacetRows(profile.facets);
	const techEntries = buildTechEntries(profile.tech, vocabulary);
	const isHiring = profile.facets?.filterGrade?.hiringWebsite === true;
	const teamSize = profile.facets?.filterGrade?.teamSize;
	const digestedAt = formatDate(profile.digestedAt);
	const language = profile.language ? (LANGUAGE_LABELS[profile.language] ?? profile.language.toUpperCase()) : null;

	return (
		<SectionCard title="Website-Profil" subtitle={digestedAt ? `Stand: ${digestedAt}` : undefined} icon={BsWindow}>
			{profile.oneLiner && <p className="mb-3 text-sm leading-relaxed text-text">{profile.oneLiner}</p>}
			<dl>
				{facetRows.map((row) => (
					<FacetRow key={row.facet} row={row} vocabulary={vocabulary} />
				))}
				{Number.isInteger(teamSize) && <InfoRow label={FACET_LABELS.teamSize} value={String(teamSize)} />}
				{isHiring && <InfoRow label={FACET_LABELS.hiringWebsite} value="Ja, laut Website" />}
				{techEntries.map((entry) => (
					<InfoRow key={entry.label} label={entry.label} value={entry.value} />
				))}
				{language && <InfoRow label="Sprache" value={language} />}
			</dl>
		</SectionCard>
	);
};

CompanyWebsiteProfile.propTypes = {
	profile: PropTypes.shape({
		oneLiner: PropTypes.string,
		facets: PropTypes.shape({
			filterGrade: PropTypes.object,
			hints: PropTypes.object,
		}),
		tech: PropTypes.object,
		language: PropTypes.string,
		digestedAt: PropTypes.string,
	}).isRequired,
};

export default CompanyWebsiteProfile;
